import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import { useFormCoreInput } from "../core/useFormCoreInput";
import { IFormInputLeadingDropdownProps } from "./FormInputLeadingDropdown";

interface IUseFormInputLeadingDropdownProps {
  id?: string;
  label: string;
  defaultSelected: IFilterItemV2;
  options: IFilterItemV2[];
  defaultValue?: string;
  placeholder?: string;
  tooltip?: ReactNode;
  hint?: ReactNode;
  clearable?: boolean;
  disabled?: boolean;
  validation?: {
    required?: boolean;
    pattern?: RegExp;
    maxLength?: number;
  };
  error?: string | null;
  onChangeInputExternal?: (value: string) => void;
  onChangeDropdownExternal?: (value: IFilterItemV2 | undefined) => void;
  onClear?: () => void;
}

export const useFormInputLeadingDropdown = ({
  id,
  label,
  defaultSelected,
  options,
  defaultValue,
  placeholder,
  tooltip,
  hint,
  disabled,
  validation,
  onChangeInputExternal,
  onChangeDropdownExternal,
}: IUseFormInputLeadingDropdownProps) => {
  // --------------- STATES ---------------

  const [selected, setSelected] = useState<IFilterItemV2>(defaultSelected);

  useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);

  const {
    value,
    reset: resetInput,
    setExternalError,
    errorText,
    renderProps: coreInputRenderProps,
  } = useFormCoreInput({
    defaultValue,
    placeholder,
    validation,
    disabled,
    onChangeExternal: onChangeInputExternal,
  });

  // --------------- METHODS ---------------

  const onChangeSelected = useCallback(
    (value: IFilterItemV2) => {
      setSelected(value);
      if (onChangeDropdownExternal) {
        onChangeDropdownExternal(value);
      }
    },
    [onChangeDropdownExternal]
  );

  const reset = () => {
    resetInput();
    setSelected(defaultSelected);
    if (onChangeInputExternal) {
      onChangeInputExternal(defaultValue ?? "");
    }
    if (onChangeDropdownExternal) {
      onChangeDropdownExternal(defaultSelected);
    }
  };

  // --------------- COMPUTED ---------------

  const renderProps = useMemo(
    () =>
      ({
        id,
        label,
        required: validation?.required,
        errorText,
        hint,
        ...coreInputRenderProps,
        tooltip,
        options,
        selected,
        onChangeSelected,
      }) satisfies IFormInputLeadingDropdownProps,
    [
      id,
      label,
      validation?.required,
      errorText,
      hint,
      coreInputRenderProps,
      tooltip,
      options,
      selected,
      onChangeSelected,
    ]
  );

  return { value, selected, reset, setExternalError, renderProps };
};
